/**
 * @module shared/lib/utils
 *
 * Grab-bag of small, cross-cutting utilities: class-name merging, tenant
 * detection/overrides for multi-tenant theming, theme application to the
 * document root, and a handful of date/label/debounce helpers.
 *
 * Functions that touch `window` or `document` are browser-only and should
 * not be invoked from server-side code paths.
 */

import { type ClassValue, clsx } from "clsx";
import { twMerge } from "tailwind-merge";

/**
 * Merges Tailwind class names, resolving conflicts so that the later class
 * wins (e.g. `"p-2 p-4"` collapses to `"p-4"`).
 *
 * @param inputs - Any mix of strings, arrays, or conditional-class objects
 *   accepted by `clsx`.
 * @returns A single space-separated class-name string.
 *
 * @example
 * <div className={cn("p-2", isActive && "bg-blue-500", className)} />
 */
export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/**
 * Resolves the active tenant id for the current browser session.
 *
 * Resolution precedence (first match wins):
 *   1. `?tenant=<id>` query parameter — when present it is also persisted to
 *      `localStorage` under the `dev-tenant` key so it survives navigation.
 *   2. `localStorage.dev-tenant` — developer/test override.
 *   3. The hostname's first subdomain label. `localhost` and `www` both
 *      resolve to the literal string `"default"`.
 *
 * @returns The resolved tenant identifier (never empty).
 */
export function detectTenant(): string {
  // Check for tenant query parameter first (for testing multi-tenancy)
  const urlParams = new URLSearchParams(window.location.search);
  const queryTenant = urlParams.get("tenant");

  if (queryTenant) {
    // Automatically persist query parameter to localStorage for testing
    localStorage.setItem('dev-tenant', queryTenant)
    return queryTenant
  }

  // Check for tenant in localStorage (for development testing)
  const storedTenant = localStorage.getItem("dev-tenant");
  if (storedTenant) {
    return storedTenant;
  }

  // Default subdomain detection
  const hostname = window.location.hostname
  const subdomain = hostname.split('.')[0]
  // Treat localhost and www as the default tenant
  const detectedTenant = (subdomain !== 'www' && subdomain !== 'localhost') ? subdomain : 'default'

  return detectedTenant
}

/**
 * Test-only helper that writes a tenant id into `localStorage.dev-tenant` so
 * subsequent `detectTenant()` calls resolve to the given tenant.
 *
 * IMPORTANT: This is intended strictly for local development and automated
 * tests. It must NOT be called from production code paths — doing so would
 * let any caller pin the user's tenant to an arbitrary value.
 *
 * @param tenantId - The tenant id to persist as an override.
 */
export function setTestTenant(tenantId: string): void {
  localStorage.setItem("dev-tenant", tenantId);
}

/**
 * Clears any `dev-tenant` override previously set by {@link setTestTenant},
 * restoring the default subdomain-based tenant resolution.
 */
export function clearTestTenant(): void {
  localStorage.removeItem("dev-tenant");
}

import { type TenantConfig } from "@/config/schemas/tenantConfig.schema";

/**
 * Converts a `#RRGGBB` hex color into a `"R G B"` space-separated triple,
 * the form Tailwind expects for CSS-variable-driven colors.
 *
 * @param hex - A hex color string (with or without the leading `#`).
 * @returns The `"r g b"` triple, or `"0 0 0"` when `hex` is malformed.
 */
export function hexToRgb(hex: string): string {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  if (!result) return "0 0 0";

  const r = parseInt(result[1], 16);
  const g = parseInt(result[2], 16);
  const b = parseInt(result[3], 16);

  return `${r} ${g} ${b}`;
}

/**
 * Applies the tenant theme to the document root by writing each color as a
 * CSS custom property on `document.documentElement`.
 *
 * Behaviour:
 *   - Values that start with `$` are treated as aliases and resolved against
 *     other keys in the same theme object (one level of indirection).
 *   - CamelCase keys are translated to kebab-case, prefixed with `--color-`
 *     (e.g. `primaryAccent` → `--color-primary-accent`).
 *   - Each hex color is converted to the `"r g b"` form expected by Tailwind
 *     utility classes that consume these variables.
 *
 * Call once on app bootstrap (after the tenant config has resolved) and
 * again whenever the tenant changes at runtime.
 *
 * @param tenant - The resolved tenant configuration.
 */
export function applyTheme(tenant: TenantConfig) {
  const root = document.documentElement;
  const { theme } = tenant;

  if (!theme) {
    return;
  }

  const themeColors = { ...theme };

  // Resolve variables
  for (const key in themeColors) {
      const value = themeColors[key as keyof typeof themeColors];
      if (typeof value === "string" && value.startsWith('$')) {
          const varName = value.substring(1) as keyof typeof themeColors;
          themeColors[key as keyof typeof themeColors] = themeColors[varName];
      }
  }

  // --- Apply all theme colors as CSS variables ---
  Object.entries(themeColors).forEach(([key, value]) => {
    if (value && typeof value === "string" && value.startsWith("#")) {
      const cssVar = `--color-${key.replace(/([A-Z])/g, "-$1").toLowerCase()}`;
      root.style.setProperty(cssVar, hexToRgb(value));
    }
  });
}

/**
 * Formats a `Date` as a short US-locale date, e.g. `"Jan 5, 2026"`.
 *
 * @param date - The date to format.
 * @returns A human-readable date string.
 */
export function formatDate(date: Date): string {
  return new Intl.DateTimeFormat("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  }).format(date);
}

/**
 * Formats a `Date` as a short US-locale time, e.g. `"09:45 AM"`.
 *
 * @param date - The date to format.
 * @returns A human-readable time string.
 */
export function formatTime(date: Date): string {
  return new Intl.DateTimeFormat("en-US", {
    hour: "2-digit",
    minute: "2-digit",
  }).format(date);
}

/**
 * Combines a `YYYY-MM-DD` date string and a `HH:mm` time string into a UTC
 * ISO 8601 timestamp.
 *
 * @param date - Date portion in `YYYY-MM-DD` form.
 * @param time - Time portion in `HH:mm` form.
 * @returns The full ISO string, or an empty string if either input cannot be
 *   parsed into numbers.
 */
export function formatToISOString(date: string, time: string): string {
  const [year, month, day] = date.split('-').map(Number);
  const [hours, minutes] = time.split(':').map(Number);

  if (isNaN(year) || isNaN(month) || isNaN(day) || isNaN(hours) || isNaN(minutes)) {
    // Return a default or throw an error if the date/time is invalid
    return '';
  }

  // Create a date object in UTC
  const utcDate = new Date(Date.UTC(year, month - 1, day, hours, minutes));

  // Return the full ISO string
  return utcDate.toISOString();
}

/**
 * Safely parses an ISO-ish datetime string and splits it into local-timezone
 * `date` and `time` components suitable for HTML `<input type="date">` /
 * `<input type="time">` fields.
 *
 * @param dateString - Source date string; `null`/`undefined`/empty returns `null`.
 * @returns `{ date: "YYYY-MM-DD", time: "HH:mm" }`, or `null` when the input
 *   is missing or cannot be parsed.
 */
export function safeFormatDate(dateString: string | null | undefined): { date: string; time: string } | null {
  if (!dateString) return null;

  const date = new Date(dateString);
  if (isNaN(date.getTime())) {
    return null;
  }

  const year = date.getFullYear();
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  const day = date.getDate().toString().padStart(2, '0');
  const hours = date.getHours().toString().padStart(2, '0');
  const minutes = date.getMinutes().toString().padStart(2, '0');

  return {
    date: `${year}-${month}-${day}`,
    time: `${hours}:${minutes}`,
  };
}

/**
 * Converts a raw API label value (e.g. snake_case, kebab-case, dot.case)
 * into a human-readable label with the first letter capitalised.
 * e.g. "first_name" → "First name", "someLabel" → "SomeLabel"
 *
 * @param value - Raw label value.
 * @returns The humanized label (or the original trimmed string if it was empty).
 */
export const customizeLabel = (value: string): string => {
  const trimmedValue = value.trim();
  if (!trimmedValue) return trimmedValue;
  if (!/[_.-]/.test(trimmedValue)) {
    return trimmedValue.charAt(0).toUpperCase() + trimmedValue.slice(1);
  }
  const normalizedValue = trimmedValue.replace(/[_.-]+/g, ' ');
  return normalizedValue.charAt(0).toUpperCase() + normalizedValue.slice(1);
};

/**
 * Returns a trailing-edge debounced wrapper around `func`. The wrapped
 * function delays invocation until `delay` ms have elapsed since the most
 * recent call; earlier pending calls are discarded.
 *
 * @param func - The function to debounce.
 * @param delay - Delay in milliseconds.
 * @returns A function with the same call signature as `func`.
 *
 * @example
 * const onSearch = debounce((q: string) => runSearch(q), 250);
 * input.addEventListener('input', e => onSearch(e.target.value));
 */
export const debounce = (func: (...args: any[]) => void, delay: number) => {
  let timeout: NodeJS.Timeout;
  return (...args: any[]) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => func.apply(this, args), delay);
  };
};

/**
 * Escapes SQL LIKE wildcard characters so user input is treated as literal text.
 * Prevents % and _ from acting as wildcards in LIKE/ILIKE queries.
 *
 * Escaping order matters: backslash must be escaped first to avoid double-escaping.
 *   \  →  \\
 *   %  →  \%
 *   _  →  \_
 */
export function escapeSqlLikePattern(value: string): string {
  return value
    .replace(/\\/g, '\\\\')
    .replace(/%/g, '\\%')
    .replace(/_/g, '\\_');
}

/**
 * Maps ISO 639-1 two-letter language codes to full English display names.
 * Mirrors the legacy drt_ui LanguageList gem used by
 * `customer.display_value(:language_code)`. Unknown codes fall back to the raw
 * code value so no data is lost.
 */
export const ISO_639_1_MAP: Record<string, string> = {
  aa: "Afar", ab: "Abkhazian", ae: "Avestan", af: "Afrikaans", ak: "Akan",
  am: "Amharic", an: "Aragonese", ar: "Arabic", as: "Assamese", av: "Avaric",
  ay: "Aymara", az: "Azerbaijani", ba: "Bashkir", be: "Belarusian", bg: "Bulgarian",
  bi: "Bislama", bm: "Bambara", bn: "Bengali", bo: "Tibetan", br: "Breton",
  bs: "Bosnian", ca: "Catalan", ce: "Chechen", ch: "Chamorro", co: "Corsican",
  cr: "Cree", cs: "Czech", cu: "Church Slavic", cv: "Chuvash", cy: "Welsh",
  da: "Danish", de: "German", dv: "Divehi", dz: "Dzongkha", ee: "Ewe",
  el: "Greek", en: "English", eo: "Esperanto", es: "Spanish", et: "Estonian",
  eu: "Basque", fa: "Persian", ff: "Fulah", fi: "Finnish", fj: "Fijian",
  fo: "Faroese", fr: "French", fy: "Western Frisian", ga: "Irish",
  gd: "Scottish Gaelic", gl: "Galician", gn: "Guaraní", gu: "Gujarati",
  gv: "Manx", ha: "Hausa", he: "Hebrew", hi: "Hindi", ho: "Hiri Motu",
  hr: "Croatian", ht: "Haitian Creole", hu: "Hungarian", hy: "Armenian",
  hz: "Herero", ia: "Interlingua", id: "Indonesian", ie: "Interlingue",
  ig: "Igbo", ii: "Sichuan Yi", ik: "Inupiaq", io: "Ido", is: "Icelandic",
  it: "Italian", iu: "Inuktitut", ja: "Japanese", jv: "Javanese",
  ka: "Georgian", kg: "Kongo", ki: "Kikuyu", kj: "Kwanyama", kk: "Kazakh",
  kl: "Kalaallisut", km: "Khmer", kn: "Kannada", ko: "Korean", kr: "Kanuri",
  ks: "Kashmiri", ku: "Kurdish", kv: "Komi", kw: "Cornish", ky: "Kyrgyz",
  la: "Latin", lb: "Luxembourgish", lg: "Ganda", li: "Limburgish", ln: "Lingala",
  lo: "Lao", lt: "Lithuanian", lu: "Luba-Katanga", lv: "Latvian",
  mg: "Malagasy", mh: "Marshallese", mi: "Māori", mk: "Macedonian",
  ml: "Malayalam", mn: "Mongolian", mr: "Marathi", ms: "Malay", mt: "Maltese",
  my: "Burmese", na: "Nauru", nb: "Norwegian Bokmål", nd: "North Ndebele",
  ne: "Nepali", ng: "Ndonga", nl: "Dutch", nn: "Norwegian Nynorsk",
  no: "Norwegian", nr: "South Ndebele", nv: "Navajo", ny: "Chichewa",
  oc: "Occitan", oj: "Ojibwe", om: "Oromo", or: "Odia", os: "Ossetian",
  pa: "Punjabi", pi: "Pāli", pl: "Polish", ps: "Pashto", pt: "Portuguese",
  qu: "Quechua", rm: "Romansh", rn: "Kirundi", ro: "Romanian", ru: "Russian",
  rw: "Kinyarwanda", sa: "Sanskrit", sc: "Sardinian", sd: "Sindhi",
  se: "Northern Sami", sg: "Sango", si: "Sinhala", sk: "Slovak",
  sl: "Slovenian", sm: "Samoan", sn: "Shona", so: "Somali", sq: "Albanian",
  sr: "Serbian", ss: "Swati", st: "Southern Sotho", su: "Sundanese",
  sv: "Swedish", sw: "Swahili", ta: "Tamil", te: "Telugu", tg: "Tajik",
  th: "Thai", ti: "Tigrinya", tk: "Turkmen", tl: "Tagalog", tn: "Tswana",
  to: "Tonga", tr: "Turkish", ts: "Tsonga", tt: "Tatar", tw: "Twi",
  ty: "Tahitian", ug: "Uyghur", uk: "Ukrainian", ur: "Urdu", uz: "Uzbek",
  ve: "Venda", vi: "Vietnamese", vo: "Volapük", wa: "Walloon", wo: "Wolof",
  xh: "Xhosa", yi: "Yiddish", yo: "Yoruba", za: "Zhuang", zh: "Chinese",
  zu: "Zulu",
};

/**
 * Resolves a full English language display name from an ISO 639-1 two-letter
 * code (case-insensitive). Falls back to the raw code when the code is
 * unrecognised so no data is lost.
 *
 * @param code - ISO 639-1 code, e.g. `"en"`, `"ES"`.
 * @returns Full display name (e.g. `"English"`) or the original code.
 */
export function getLanguageFromCode(code: string): string {
  if (!code) return "";
  return ISO_639_1_MAP[code.toLowerCase()] ?? code;
}
